import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';
import {
  Cell,
  Column,
  ColumnInstance,
  HeaderGroup,
  Row as RowType,
  TableCellProps,
  TableHeaderProps,
  TableInstance,
  TableOptions,
  TableToggleAllRowsSelectedProps,
  TableToggleHideAllColumnProps,
  useBlockLayout,
  useExpanded,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import clsx from 'clsx';
import styled from 'styled-components';

import { ACTIONS_HEADER_ID, SELECT_HEADER_ID } from '../../routes/portfolio/components/OptimizationProposal/columns';
import SvgSort from 'components/icons/Sort2';
import { SvgPin } from 'components/icons/Pin';

import { colorByKey } from 'theme/utils';

import { nexyColors } from '../../theme';
import ButtonIcon from '../ButtonIcon';
import Tooltip from '../Tooltip';
import { ButtonSortStyled } from './ButtonSortStyled';
import { Row } from './Row';
import { getStickyColumnIds, isInPinnedTree } from './stickyColumns';
import { getInitialHiddenColumnsByUser } from './cacheHiddenColumns';
import { sortTypes } from './sortTypes';
import { IColumnProps } from './tableTypes';
import Checkbox from '../Checkbox';
import { cn } from '../../lib/utils';
import { PaginationControls } from './PaginationControls';
import useUserStore from '../../store/user';

const TABLE_VERSION = 1;
const TABLE_MANAGER_WIDTH = 32;

export const classes = {
  container: 'NEXYTableContainer',
  header: 'NEXYTableHeader',
  row: 'NEXYTableRow',
};

interface TableProps<D extends object = {}> extends TableOptions<D> {
  disableManager?: boolean;
  disableExpanded?: boolean;
  disableSort?: boolean;
  disablePagination?: boolean;
  disableRowSelection?: boolean;
  /**
   * Opt-in: round-trip the active sort through the per-table localStorage blob so it survives an
   * unmount/remount (e.g. the revamp Performance table swaps in a loading skeleton on every date-range
   * refetch, remounting this component fresh). Default false keeps every other table's behaviour
   * unchanged (sort lives only in component state).
   */
  persistSort?: boolean;
  onSelectedRowsChange?: (rows: RowType<D>[]) => void;
  tableId?: string;
  getCustomCellStyles?: (cell: Column<IColumnProps>, row?: RowType, column?: any) => React.CSSProperties;
  renderTableManager?: (props: TableManagerProps) => JSX.Element;
  defaultPageSize?: number;
}

interface TableManagerProps {
  setStickyColumns: (value: ((prevState: string[]) => string[]) | string[]) => void;
  columns: Array<ColumnInstance<{}>>;
  getToggleHideAllColumnsProps: (props?: Partial<TableToggleHideAllColumnProps>) => TableToggleHideAllColumnProps;
  rows: Array<RowType<{}>>;
  toggleHideAllColumns: (value?: boolean) => void;
  stickyColumns: string[];
}

// Define a type that includes pagination properties
type TableInstanceWithPagination<D extends object = {}> = TableInstance<D> & {
  page: Array<RowType<D>>;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageOptions: number[];
  pageCount: number;
  gotoPage: (updater: ((pageIndex: number) => number) | number) => void;
  nextPage: () => void;
  previousPage: () => void;
  setPageSize: (pageSize: number) => void;
  selectedFlatRows: RowType<D>[];
  toggleRowSelected: (rowId: string, set?: boolean) => void;
  toggleAllRowsSelected: (value?: boolean) => void;
  getToggleAllPageRowsSelectedProps: () => TableToggleAllRowsSelectedProps;
  getToggleRowSelectedProps: (row: RowType<D>) => TableToggleAllRowsSelectedProps;
  state: {
    pageIndex: number;
    pageSize: number;
    selectedRowIds: Record<string, boolean>;
  };
};

export const ExtendedTable: React.FC<TableProps> = ({
  columns,
  data,
  disableManager,
  disableSort,
  getCustomCellStyles,
  renderTableManager,
  disableExpanded = true,
  disablePagination = true,
  disableRowSelection = true,
  onSelectedRowsChange,
  tableId,
  defaultPageSize = 50,
  persistSort = false,
}) => {
  const hooks: any[] = [useBlockLayout, useResizeColumns, useGlobalFilter];

  if (!disableSort) hooks.push(useSortBy);
  if (!disableExpanded) hooks.push(useExpanded);
  if (!disablePagination) hooks.push(usePagination);
  if (!disableRowSelection) hooks.push(useRowSelect);

  const match = useRouteMatch<{ portfolioID: string }>();
  const portfolioId = parseInt(match.params.portfolioID, 10);

  const { user } = useUserStore();
  const localStorageKey = `${tableId}-${TABLE_VERSION}-tableManager-${user?.user_id}-${portfolioId}`;

  const localStorageData = getInitialHiddenColumnsByUser(localStorageKey);
  const [stickyColumns, setStickyColumns] = useState(localStorageData.stickyColumns || []);
  const [columnWidths, setColumnWidths] = useState(localStorageData.columnWidths || {});
  // When persistSort is on, seed from the saved sort so it survives a remount (loading-skeleton swap)
  // or reload; otherwise start unsorted (legacy behaviour).
  const [sortState, setSortState] = useState(persistSort ? localStorageData.sortBy || [] : []);

  const tableInstance = useTable(
    {
      columns,
      data,
      sortTypes,
      autoResetExpanded: false,
      autoResetResize: false,
      autoResetPage: false,
      initialState: {
        hiddenColumns: localStorageData.hiddenColumns,
        // @ts-expect-error
        columnResizing: { columnWidths: localStorageData.columnWidths || {} },
        sortBy: sortState,
        pageIndex: 0,
        pageSize: defaultPageSize,
      },
      stateReducer: handleStateReducer,
    },
    ...hooks,
    (hooks) => {
      if (!disableRowSelection) {
        hooks.columns.push((columns) => [
          {
            accessor: SELECT_HEADER_ID,
            id: SELECT_HEADER_ID,
            disableSortBy: true,
            Header: '',
            width: 45,
            isHiddenInManager: true,
            className: 'border-right !p-0',
            columns: [
              {
                width: 45,
                className: 'border-right !p-0',
                isHiddenInManager: true,
                disableSortBy: true,

                id: 'selection',
                // The header can use the table's getToggleAllRowsSelectedProps method or  getToggleAllPageRowsSelectedProps for paginated selection
                // @ts-expect-error
                Header: ({ getToggleAllRowsSelectedProps }) => (
                  <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                ),
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a checkbox
                Cell: ({ row }) => (
                  // @ts-expect-error
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                ),
              },
            ],
          },

          ...columns,
        ]);
      }
    },
  ) as TableInstanceWithPagination;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    allColumns,
    state: { hiddenColumns, pageIndex, pageSize, selectedRowIds },
    setHiddenColumns,
    selectedFlatRows,
    getToggleHideAllColumnsProps,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = tableInstance;

  useEffect(() => {
    const blob: Record<string, unknown> = { hiddenColumns, stickyColumns, columnWidths };
    // Only opted-in tables persist their sort, so other tables' blobs keep their existing shape.
    if (persistSort) blob.sortBy = sortState;
    localStorage.setItem(localStorageKey, JSON.stringify(blob));
  }, [hiddenColumns, stickyColumns, columnWidths, localStorageKey, persistSort, sortState]);

  useEffect(() => {
    if (selectedFlatRows && onSelectedRowsChange && !disableRowSelection) {
      onSelectedRowsChange(selectedFlatRows);
    }
  }, [selectedFlatRows, onSelectedRowsChange]);

  ensureNotAllHidden(allColumns);

  const toggleHideAllColumns = () => {
    // @ts-expect-error
    const hideableColumns = allColumns.filter((column) => !column.disableHiding).map((column) => column.id);
    const allHideableColumnsHidden = hideableColumns.every((columnId) => hiddenColumns.includes(columnId));

    if (allHideableColumnsHidden) {
      // Unhide all hideable columns
      setHiddenColumns(hiddenColumns.filter((columnId) => !hideableColumns.includes(columnId)));
    } else {
      // Hide all hideable columns
      setHiddenColumns([...hiddenColumns, ...hideableColumns]);
    }
  };

  const displayRows = !disablePagination
    ? getPaginatedRows(
        // @ts-expect-error
        rows.filter((row) => !row.depth),
        pageIndex,
        pageSize,
      )
    : rows;

  return (
    <>
      <StyledTable className={classes.container} enableSticky={true}>
        <table {...getTableProps()}>
          <TableHeader
            headerGroups={headerGroups}
            disableSort={disableSort}
            disableManager={disableManager}
            renderTableManager={renderTableManager}
            getCustomCellStyles={getCustomCellStyles}
            getStylingByHeader={getStylingByHeader}
            allColumns={allColumns}
            getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
            toggleHideAllColumns={toggleHideAllColumns}
            setStickyColumns={setStickyColumns}
            stickyColumns={stickyColumns}
            rows={displayRows}
          />
          <TableBody
            getTableBodyProps={getTableBodyProps}
            rows={displayRows}
            prepareRow={prepareRow}
            disableManager={disableManager}
            getCustomCellStyles={getCustomCellStyles}
            getCellStyling={getCellStyling}
          />
        </table>
      </StyledTable>
      <div className={cn('flex w-full', !disableRowSelection ? 'justify-between' : 'justify-end')}>
        {!disableRowSelection && (
          <div className="flex items-center py-3 font-light text-neutral-500">
            <p>
              {Object.keys(selectedRowIds).length} of {rows?.length} row(s) selected
            </p>
          </div>
        )}
        {!disablePagination ? (
          <PaginationControls
            gotoPage={gotoPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageSize={pageSize}
            setPageSize={setPageSize}
            nextPage={nextPage}
            previousPage={previousPage}
          />
        ) : null}
      </div>
    </>
  );

  function handleStateReducer(newState, action) {
    switch (action.type) {
      case 'toggleSortBy':
        setSortState(newState.sortBy);
        break;
      case 'columnResizing':
        const columnId = Object.keys(newState.columnResizing.columnWidths)[0];
        const contentColumn = document.querySelector(`[data-column-id="${columnId}"]`);

        if (contentColumn && !newState.columnResizing.columnWidths[columnId]) {
          const currentWidth = contentColumn.getBoundingClientRect().width;
          newState.columnResizing.columnWidth = currentWidth;
          newState.columnResizing.columnWidths[columnId] = currentWidth;
          newState.columnResizing.headerIdWidths = [[columnId, currentWidth]];
        }

        setColumnWidths({ ...newState.columnResizing.columnWidths });
        break;
    }
    return newState;
  }

  function getStylingByHeader(header: HeaderGroup<IColumnProps>) {
    const isSticky = (header: HeaderGroup<IColumnProps>) => isInPinnedTree(header, stickyColumns);

    if (isSticky(header)) {
      const headers = headerGroups.flatMap((headerGroup) => headerGroup.headers);
      const ownIndex = headers.indexOf(header);
      const stickyLeft = headers
        .slice(0, ownIndex)
        .filter((headerArg) => isSticky(headerArg) && headerArg.depth === header.depth)
        .map((header) => header.totalWidth)
        .reduce((partialSum, a) => partialSum + a, 0);

      return {
        className: 'sticky',
        style: {
          left: stickyLeft.toString() + 'px',
          boxShadow: '2px 0px 2px 0px rgba(0, 0, 0, 0.05)',
          zIndex: 10,
          overflow: 'visible',
        },
      };
    }
    return {};
  }

  function getCellStyling(cell: Cell<IColumnProps>) {
    const headers = headerGroups.flatMap((headerGroup) => headerGroup.headers);
    const header = headers.find((header) => header.id === cell.column.id);
    return getStylingByHeader(header);
  }
};

const TableHeader = ({
  headerGroups,
  disableSort,
  disableManager,
  renderTableManager,
  getCustomCellStyles,
  getStylingByHeader,
  allColumns,
  getToggleHideAllColumnsProps,
  toggleHideAllColumns,
  setStickyColumns,
  stickyColumns,
  rows,
}) => {
  // Find the first column to insert the table manager once
  const managerColumnId = React.useMemo(() => {
    const firstGroup = headerGroups[0];
    if (!firstGroup) return null;

    const column = firstGroup.headers.find(
      (col) => col.originalId === ACTIONS_HEADER_ID || firstGroup.headers.indexOf(col) === 0,
    );
    return column?.id;
  }, [headerGroups]);

  return (
    <thead className={classes.header}>
      {headerGroups.map((headerGroup, index) => (
        <tr {...headerGroup.getHeaderGroupProps()} key={index}>
          {headerGroup.headers.map((column) => {
            const sortStyles = !disableSort ? column.getSortByToggleProps() : null;
            const stickyStyles = getStylingByHeader(column);

            const headerProps = column.getHeaderProps();
            ensureEvenWidth(headerProps);
            const customStyles = getCustomCellStyles ? getCustomCellStyles(column) : {};
            const shouldRenderManager = !disableManager && column.id === managerColumnId;

            // Inline freeze ("pin") control: only on columns that opt in via `enablePin`, and only when
            // the Table Manager (which carries its own pin) is hidden, to avoid a double affordance.
            const enablePin = !!disableManager && !!column['enablePin'];
            const stickyIds = enablePin ? getStickyColumnIds(column) : [];
            const isPinned = enablePin && stickyIds.some((id) => stickyColumns.includes(id));
            const handlePinToggle = (event: React.MouseEvent) => {
              // Don't let the click bubble to the header's sort toggle.
              event.stopPropagation();
              setStickyColumns((prev: string[]) =>
                isPinned ? prev.filter((id) => !stickyIds.includes(id)) : Array.from(new Set([...prev, ...stickyIds])),
              );
            };

            const isColumnSortable = !disableSort && !column['disableSortBy'];
            const ariaSort = isColumnSortable
              ? column['isSorted']
                ? column['isSortedDesc']
                  ? 'descending'
                  : 'ascending'
                : 'none'
              : undefined;

            const handleSortKeyDown = (event: React.KeyboardEvent) => {
              if (!isColumnSortable || !sortStyles?.onClick) return;
              if (event.key === 'Enter' || event.key === ' ') {
                // Space would otherwise scroll the page; both keys toggle sort.
                event.preventDefault();
                sortStyles.onClick(event);
              }
            };

            return (
              <th
                className={clsx(column['className'] ?? '', stickyStyles.className ?? '')}
                {...headerProps}
                key={column['id']}
                data-column-id={column['id']}
                aria-sort={ariaSort}
                style={{
                  ...headerProps.style,
                  ...customStyles,
                  ...(shouldRenderManager ? { display: 'flex', justifyContent: 'flex-start' } : {}),
                  ...stickyStyles.style,
                }}
              >
                {shouldRenderManager &&
                  renderTableManager({
                    columns: allColumns,
                    getToggleHideAllColumnsProps,
                    toggleHideAllColumns,
                    setStickyColumns,
                    stickyColumns,
                    rows,
                  })}
                {/* Rest of the header cell content */}
                {column['enableColumnResize'] && (
                  <div
                    {...column.getResizerProps()}
                    data-resizer={column.id}
                    title="Resize column"
                    className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                  />
                )}
                <Row
                  {...sortStyles}
                  title=""
                  style={{ height: '100%' }}
                  isSortable={!column['disableSortBy']}
                  {...column.rowProps}
                >
                  {column.render('Header')}{' '}
                  {!column['disableSortBy'] && (
                    <ButtonSortStyled
                      type="button"
                      style={{ justifySelf: 'start', position: 'static' }}
                      asc={column['isSorted'] ? !column['isSortedDesc'] : null}
                      desc={column['isSortedDesc']}
                      // Only label it a sort control when the column is actually sortable (a table-wide
                      // `disableSort` leaves the icon but makes it inert — don't claim "Sort by X" then).
                      aria-label={
                        isColumnSortable
                          ? `Sort by ${typeof column['Header'] === 'string' ? column['Header'] : column['id']}`
                          : undefined
                      }
                      onKeyDown={handleSortKeyDown}
                    >
                      <SvgSort style={{ width: 12, height: 10.5 }} />
                    </ButtonSortStyled>
                  )}
                  {enablePin && (
                    <Tooltip content={isPinned ? 'Unpin column' : 'Pin column'} placement="top" variant="dark">
                      <ButtonIcon
                        type="button"
                        active={isPinned}
                        aria-label={isPinned ? 'Unpin column' : 'Pin column'}
                        aria-pressed={isPinned}
                        onClick={handlePinToggle}
                        style={{ marginLeft: 'auto', padding: 2 }}
                      >
                        <SvgPin style={{ width: 16, height: 16 }} fill={isPinned ? undefined : '#bbb'} />
                      </ButtonIcon>
                    </Tooltip>
                  )}
                </Row>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
};

const TableBody = ({ getTableBodyProps, rows, prepareRow, disableManager, getCustomCellStyles, getCellStyling }) => {
  return (
    <tbody {...getTableBodyProps()}>
      {rows.map((row, idx) => {
        let rowClassList = [classes.row];
        if (row.original['classList']) rowClassList = [...rowClassList, ...row.original['classList']];
        if (row.original?.['highlight']) rowClassList.push('highlight');
        prepareRow(row);
        return (
          <tr className={clsx(rowClassList)} {...row.getRowProps()} key={idx}>
            {disableManager ? null : (
              <td
                className={clsx([row.original?.['highlight'] ? 'highlight' : null])}
                style={{ ...tableManagerColStyle, width: 0, padding: 0, minWidth: 0 }}
              />
            )}
            {row.cells.map((cell) => {
              const classList = [];
              const styling = getCellStyling(cell);
              if (styling.className) classList.push(styling.className);
              if (cell.column['className']) classList.push(cell.column['className']);
              if (row.original?.['highlight']) classList.push('highlight');
              if (row.original?.['lightHighlight']) classList.push('lightHighlight');
              const cellProps = cell.getCellProps({
                className: clsx(classList),
                style: styling.style,
              });
              ensureEvenWidth(cellProps);
              const customStyles = getCustomCellStyles ? getCustomCellStyles(cell.column, row, cell) : {};
              return (
                <td
                  className={clsx(
                    cell.column.isResizing ? 'isResizing' : '',
                    cell.column['enableColumnResize'] ? 'columnResizable' : '',
                    cell.column['className'] ?? '',
                  )}
                  {...cellProps}
                  key={cell?.column?.id}
                  style={{ ...cellProps.style, ...customStyles }}
                >
                  {cell.render('Cell')}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }: { indeterminate?: boolean }, ref: React.Ref<HTMLInputElement>) => {
    const defaultRef = React.useRef<HTMLInputElement>(null);
    const resolvedRef = (ref || defaultRef) as React.RefObject<HTMLInputElement>;

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate ?? false;
      }
    }, [resolvedRef, indeterminate]);

    return (
      <div className="flex h-full items-center justify-center">
        <Checkbox type="checkbox" ref={resolvedRef} {...rest} />
      </div>
    );
  },
);

const ensureNotAllHidden = (columns: ColumnInstance<IColumnProps>[]) => {
  const allHidden = !columns.some((col) => col.isVisible);
  if (allHidden) columns[0].toggleHidden();
};

const ensureEvenWidth = (props: TableCellProps | TableHeaderProps) => {
  if (props.style.width && !props.style.minWidth) props.style.minWidth = props.style.width;
  if (props.style.width && !props.style.maxWidth) props.style.maxWidth = props.style.width;
};

// Helper to get paginated rows for top-level only
function getPaginatedRows(rows, pageIndex, pageSize) {
  const start = pageIndex * pageSize;
  const end = start + pageSize;
  const paginated = rows.slice(start, end);

  const flattenedRows = [];
  paginated.forEach((row) => {
    flattenedRows.push(row);
    if (row.isExpanded) {
      row.subRows.forEach((subRow) => flattenedRows.push(subRow));
    }
  });

  return flattenedRows;
}

const tableManagerColStyle = {
  width: TABLE_MANAGER_WIDTH.toString() + 'px',
  minWidth: TABLE_MANAGER_WIDTH.toString() + 'px',
};

const StyledTable = styled.div<any>`
  overflow-x: scroll;
  position: relative;

  .sticky {
    position: ${(props) => (props.enableSticky ? 'sticky' : 'initial')} !important;
    left: 0;
    top: 0;
    background-color: white;
    z-index: 1230;
  }
  .alwaysSticky {
    position: sticky;
    left: 0;
    top: 0;
  }

  table {
    margin: 0;
    border-spacing: 0;
    border-collapse: collapse;
    text-align: right;
    position: relative;
    width: 100%;

    th {
      padding: 0.25rem 1rem;
      min-height: 20px;
      color: ${nexyColors.raisinBlack};
      align-items: center;
      border-width: 0 0px 1px 0;
      border-style: solid;
      border-color: rgb(42 42 50 / 8%);

      font-size: 11px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 12px */
      letter-spacing: 0.24px;
      width: 100%;

      :first-child {
        padding: 0;
      }

      :last-child {
        border-width: 0 0 1px 0;
      }
      position: relative;
      .resizer {
        display: inline-block;
        width: 5px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(50%);
        z-index: 1;
        ${'' /* prevents from scrolling while dragging on touch devices */}
        touch-action: none;
        user-select: none;

        &:hover {
          background: ${colorByKey('lavender')};
        }

        &.isResizing {
          width: 4px;
          background: ${colorByKey('purpleish')};
        }
      }
    }

    tr {
      align-items: stretch;
      min-height: 20px;

      border-width: 0 0 1px 0;
      border-style: solid;
      border-color: rgb(42 42 50 / 8%);
      width: 100%;

      :last-child {
        border-width: 0;
      }
    }

    /* Body data rows get a subtle hover tint so the row under the cursor is
       readable across the wide metric grid. The static .highlight /
       .lightHighlight rules use !important and so are unaffected; expanded
       group rows are tinted via inline cell styles which also win. */
    tbody tr:hover {
      background-color: ${nexyColors.paleGrey50};
    }

    td {
      margin: 0;
      padding: 0.5rem 0.75rem;
      align-items: center;
      min-height: 20px;
      border-width: 0;
      overflow-wrap: break-word;
      overflow: hidden !important;
      width: 100%;

      :first-child {
        border-width: 0;
      }
      :last-child {
        border-width: 0;
      }
    }

    .highlight {
      background-color: ${nexyColors.seasalt} !important;
    }

    .lightHighlight {
      background-color: ${nexyColors.ghostWhite} !important;
    }

    .isResizing {
      border-color: ${colorByKey('purpleish')};
      border-right-width: 2px;
    }
  }
`;
